// src/services/auth.ts
// Step 1: real backend POST /api/auth/sign-in → accessToken (held in memory pending 2FA).
// Step 2: client-side 2FA check (TOTP / email code, configured via ProfileModal).
// Only after both steps pass is the token committed to sessionStorage.

import { API_BASE } from '../config';

// Server-side 2FA: sign-in returns either full tokens (no 2FA) or
// { require2fa, pendingToken }. The full token is issued only after the
// backend verifies the TOTP code at /verify-2fa-login.
export type LoginStartResponse = {
  require2fa: boolean;
};

export type AuthProfile = {
  email: string;
  password: string;
};

// ===== localStorage keys =====
const TOKEN_KEY = 'authToken';

// Short-lived 2FA-pending token, held between the password step and the TOTP step.
let _pendingToken: string | null = null;

// Serialises concurrent refresh attempts so only one request is in-flight.
let _refreshPromise: Promise<boolean> | null = null;

// ===== token management =====
function setAuthToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

function getAuthToken(): string | null {
  // Migrate legacy localStorage token on first read
  const legacy = localStorage.getItem(TOKEN_KEY);
  if (legacy) {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.setItem(TOKEN_KEY, legacy);
  }
  return sessionStorage.getItem(TOKEN_KEY);
}

function removeAuthToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

export function getAuthHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ===== token refresh =====

// Calls POST /api/auth/refresh (uses httpOnly refreshToken cookie automatically).
// Returns true on success (new accessToken stored), false if refresh token is expired.
async function refreshOnce(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = await res.json() as { accessToken?: string };
      if (!data.accessToken) return false;
      setAuthToken(data.accessToken);
      return true;
    } catch {
      return false;
    }
  })().finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

// Authenticated fetch wrapper: adds Bearer token, handles 401 with one refresh+retry.
// On refresh failure: clears session and redirects to /login.
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const makeRequest = () =>
    fetch(url, {
      ...options,
      headers: {
        ...((options.headers as Record<string, string>) ?? {}),
        ...getAuthHeader(),
      },
      credentials: 'include',
    });

  const res = await makeRequest();
  if (res.status !== 401) return res;

  const refreshed = await refreshOnce();
  if (!refreshed) {
    await logout();
    window.location.replace('/login');
    throw new Error('Сессия истекла. Выполняется перенаправление на страницу входа.');
  }

  return makeRequest();
}

export async function logout(): Promise<void> {
  // Clear the local session FIRST (synchronously) so isAuthenticated() flips to
  // false immediately — callers that navigate right after logout() without
  // awaiting it must see the cleared state, otherwise PublicOnlyRoute bounces
  // them back and logout appears to need a second click.
  removeAuthToken();
  _pendingToken = null;
  // Then notify the backend (fire-and-forget; local session is already gone).
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    // network error is fine — local session already cleared
  }
}

export { setAuthToken };

// ===== profile (ProfileModal: change email / password) =====
export function getAuthProfile(): AuthProfile {
  return { email: '', password: '' };
}

export async function saveAuthProfile(patch: Partial<AuthProfile>): Promise<void> {
  const hasEmail    = typeof patch.email === 'string' && patch.email.trim().length > 0;
  const hasPassword = typeof patch.password === 'string' && patch.password.length > 0;

  if (hasEmail || hasPassword) {
    const body: Record<string, string> = {};
    if (hasEmail)    body.email       = (patch.email as string).trim();
    if (hasPassword) body.newPassword = patch.password as string;

    const res = await fetchWithAuth(`${API_BASE}/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as any).message ?? 'Ошибка при сохранении профиля');
    }
  }
}

// ===== login flow =====

// Step 1 — verify credentials. Backend returns either full tokens (no 2FA) or
// { require2fa, pendingToken }. With 2FA, the access token is issued only after
// verifyTotpLogin() succeeds — the requirement is decided server-side (DB).
export async function loginStart(
  email: string,
  password: string,
): Promise<LoginStartResponse> {
  const res = await fetch(`${API_BASE}/auth/sign-in`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email: email.trim(), password }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    const msg = (data.message ?? '').replace(/^Error:\s*/i, '');
    throw new Error(msg || 'Неверный e-mail или пароль');
  }

  const data = await res.json() as {
    accessToken?: string;
    require2fa?: boolean;
    pendingToken?: string;
  };

  if (data.require2fa && data.pendingToken) {
    _pendingToken = data.pendingToken;
    return { require2fa: true };
  }

  if (data.accessToken) {
    setAuthToken(data.accessToken);
    _pendingToken = null;
    return { require2fa: false };
  }

  throw new Error('Сервер не вернул токен авторизации');
}

// Step 2 (only when require2fa) — send the TOTP code with the pending token; the
// backend verifies it server-side and returns the real access token.
export async function verifyTotpLogin(totp: string): Promise<void> {
  if (!_pendingToken) throw new Error('Сессия истекла. Войдите заново.');

  const res = await fetch(`${API_BASE}/auth/verify-2fa-login`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body:    JSON.stringify({ pendingToken: _pendingToken, totp }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(data.message || 'Неверный код Google Authenticator');
  }

  const data = await res.json() as { accessToken?: string };
  if (!data.accessToken) throw new Error('Сервер не вернул токен авторизации');

  setAuthToken(data.accessToken);
  _pendingToken = null;
}

// ── TOTP management (requires active accessToken) ─────────────────────────

export async function setupTotp(): Promise<{ qrUrl: string; secret: string }> {
  const res = await fetchWithAuth(`${API_BASE}/auth/totp/setup`, { method: 'POST' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(data.message || 'Ошибка настройки TOTP');
  }
  return res.json() as Promise<{ qrUrl: string; secret: string }>;
}

export async function confirmTotp(token: string): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/auth/totp/confirm`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ token }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(data.message || 'Неверный код');
  }
}

export async function removeTotp(): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/auth/totp`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Ошибка удаления TOTP');
}

// Whether TOTP 2FA is currently enabled for the logged-in admin.
export async function getTotpStatus(): Promise<boolean> {
  try {
    const res = await fetchWithAuth(`${API_BASE}/auth/totp/status`);
    if (!res.ok) return false;
    const data = await res.json() as { enabled?: boolean };
    return !!data.enabled;
  } catch {
    return false;
  }
}
